import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import {
  toggleCategory,
  toggleBrand,
  toggleCategoryTwo,
  toggleCategoryThree,
  toggleCategoryFour,
  toggleCategoryFive,
  setSelectedCustomer,
} from "../../../../redux/IchthusSlice";
import PaginationPos from "./PaginationPos";
import ProductPos from "./ProductPos";
import SearchableFilter from "./SearchableFilter";
import CustomerDisplay from "./CustomerDisplay";
import FormInputs from "./FormInputs";
import { domain } from "../../../../security";
import {
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  X,
  User,
  SlidersHorizontal,
  Users,
  Tag,
  RotateCcw,
} from "lucide-react";
import { Range } from "react-range";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
  }).format(value);

const AllPos = () => {
  const dispatch = useDispatch();

  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [isFilterSectionVisible, setIsFilterSectionVisible] = useState(false);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [currentRange, setCurrentRange] = useState([0, 0]);

  const posProducts = useSelector((s) => s.orebiReducer.posProducts);
  const selectedCustomer = useSelector((s) => s.orebiReducer.selectedCustomer);

  // ── Filter state selectors ──
  const checkedCategories = useSelector(
    (s) => s.orebiReducer.checkedCategories,
  );
  const checkedCategoriesTwo = useSelector(
    (s) => s.orebiReducer.checkedCategoriesTwo,
  );
  const checkedBrands = useSelector((s) => s.orebiReducer.checkedBrands);
  const checkedCategoriesThree = useSelector(
    (s) => s.orebiReducer.checkedCategoriesThree,
  );
  const checkedCategoriesFour = useSelector(
    (s) => s.orebiReducer.checkedCategoriesFour,
  );
  const checkedCategoriesFive = useSelector(
    (s) => s.orebiReducer.checkedCategoriesFive,
  );

  const isPriceFiltered =
    priceRange[0] < priceRange[1] &&
    (currentRange[0] > priceRange[0] || currentRange[1] < priceRange[1]);

  const hasActiveFilters =
    (checkedCategories?.length ?? 0) > 0 ||
    (checkedCategoriesTwo?.length ?? 0) > 0 ||
    (checkedBrands?.length ?? 0) > 0 ||
    (checkedCategoriesThree?.length ?? 0) > 0 ||
    (checkedCategoriesFour?.length ?? 0) > 0 ||
    (checkedCategoriesFive?.length ?? 0) > 0 ||
    isPriceFiltered;

  const activeFilterCount =
    (checkedCategories?.length ?? 0) +
    (checkedCategoriesTwo?.length ?? 0) +
    (checkedBrands?.length ?? 0) +
    (checkedCategoriesThree?.length ?? 0) +
    (checkedCategoriesFour?.length ?? 0) +
    (checkedCategoriesFive?.length ?? 0) +
    (isPriceFiltered ? 1 : 0);

  // ── Reset all filters without needing a new Redux action ──
  const handleResetFilters = useCallback(() => {
    checkedCategories?.forEach((id) => dispatch(toggleCategory(id)));
    checkedCategoriesTwo?.forEach((id) => dispatch(toggleCategoryTwo(id)));
    checkedBrands?.forEach((id) => dispatch(toggleBrand(id)));
    checkedCategoriesThree?.forEach((id) => dispatch(toggleCategoryThree(id)));
    checkedCategoriesFour?.forEach((id) => dispatch(toggleCategoryFour(id)));
    checkedCategoriesFive?.forEach((id) => dispatch(toggleCategoryFive(id)));
    setCurrentRange(priceRange);
  }, [
    checkedCategories,
    checkedCategoriesTwo,
    checkedBrands,
    checkedCategoriesThree,
    checkedCategoriesFour,
    checkedCategoriesFive,
    priceRange,
    dispatch,
  ]);

  const [refreshCustomerData, setRefreshCustomerData] = useState(
    () => () => {},
  );

  const handleCustomerSelect = (customer) => {
    dispatch(
      setSelectedCustomer({
        customerId: customer?.id,
        customerName: customer?.customerName,
        address: customer?.address,
        businessStyle: customer?.businessStyle,
        customerType: customer?.customerType,
        mobileNumber: customer?.mobileNumber,
        rfid: customer?.rfid,
        tinNumber: customer?.tinNumber,
        ewt: customer?.ewt,
      }),
    );
  };

  const handleSaveCustomerForm = async () => {
    if (!selectedCustomer?.customerId) {
      alert("No customer selected.");
      return;
    }
    try {
      await axios.delete(`${domain}/api/CustomerTemps/delete-all`, {
        headers: { "Content-Type": "application/json" },
      });
      const res = await axios.post(
        `${domain}/api/CustomerTemps`,
        selectedCustomer,
        {
          headers: { "Content-Type": "application/json" },
        },
      );
      if (res.status === 200) {
        refreshCustomerData();
        alert("Customer saved successfully!");
        setIsCustomerModalOpen(false);
      }
    } catch (e) {
      console.error(e);
      alert("Failed to save customer.");
    }
  };

  const handlePriceRangeChange = useCallback((r) => {
    setPriceRange(r);
    setCurrentRange(r);
  }, []);

  const totalItems = posProducts.reduce((a, i) => a + i.quantity, 0);

  const now = new Date();
  const timeStr = now.toLocaleTimeString("en-PH", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div className="min-h-screen bg-gray-100 font-sans pb-28">
      {/* ── HEADER ── */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-gray-200 flex items-center px-6 h-16 justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm">
            P
          </div>
          <span className="hidden sm:block text-xs font-semibold tracking-widest uppercase text-gray-400">
            POS Terminal
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 font-mono">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          <span>LIVE · {timeStr}</span>
        </div>
      </header>

      {/* ── MAIN ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 flex flex-col gap-4">
        {/* ── CUSTOMER PANEL ── */}
        {/* <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all duration-200 overflow-hidden z-20">
          <div className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-indigo-200 rounded-tl-xl pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-7 h-7 border-b-2 border-r-2 border-indigo-200 rounded-br-xl pointer-events-none" />

          <div className="flex items-center justify-between px-5 py-3 bg-gray-50/70 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              <span className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">
                Customer
              </span>
            </div>
            <button
              onClick={() => setIsCustomerModalOpen(true)}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 transition-all"
            >
              <User size={12} />
              {selectedCustomer ? "Edit Customer" : "Select Customer"}
            </button>
          </div>

          <div className="px-5 py-4">
            <CustomerDisplay
              onRefresh={(fn) => setRefreshCustomerData(() => fn)}
            />
          </div>
        </div> */}

        {/* ── PRODUCT CATALOG PANEL ── */}
        <div className="relative bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-indigo-100 transition-all duration-200 overflow-hidden">
          <div className="absolute top-0 left-0 w-7 h-7 border-t-2 border-l-2 border-indigo-200 rounded-tl-xl pointer-events-none" />

          <div className="flex items-center justify-between px-5 py-3 bg-gray-50/70 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
              <span className="text-[10px] font-semibold tracking-widest uppercase text-gray-400">
                Product Catalog
              </span>
            </div>

            {/* ── Advanced Search button + optional Reset pill ── */}
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <button
                  onClick={handleResetFilters}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-red-200 bg-red-50 text-red-500 hover:bg-red-100 hover:border-red-300 hover:text-red-600 transition-all"
                >
                  <RotateCcw size={11} />
                  Reset
                </button>
              )}

              <button
                onClick={() => setIsFilterSectionVisible(true)}
                className="relative flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-indigo-50 hover:border-indigo-300 hover:text-indigo-600 transition-all"
              >
                <SlidersHorizontal size={12} />
                Advanced Search
                {/* Active filter badge */}
                {hasActiveFilters && (
                  <span className="absolute -top-1.5 -right-1.5 h-4 min-w-[1rem] px-0.5 bg-indigo-500 text-white rounded-full flex items-center justify-center text-[9px] font-bold leading-none">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <div className="min-h-[520px]">
            <PaginationPos
              itemsPerPage={12}
              onPriceRangeChange={handlePriceRangeChange}
              currentPriceRange={currentRange}
            />
          </div>
        </div>
      </main>

      {/* ── FILTER MODAL ── */}
      {isFilterSectionVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col border border-gray-200">
            {/* Head */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100 rounded-t-2xl shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                  <SlidersHorizontal size={15} />
                </div>
                <div>
                  <p className="font-bold text-gray-900 leading-tight">
                    Advanced Search &amp; Filters
                  </p>
                  <p className="text-[10px] text-gray-400 tracking-wide mt-0.5">
                    Narrow down products by category, brand, and price
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsFilterSectionVisible(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 min-h-0">
              <div className="mb-2">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-1 h-4 rounded-full bg-indigo-500" />
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                    Primary Filters
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <SearchableFilter
                    title="Categories"
                    apiEndpoint="/api/Categories"
                    dataKey="categoryName"
                    placeholder="Category..."
                    reduxSelector={(s) => s.orebiReducer.checkedCategories}
                    toggleAction={toggleCategory}
                  />
                  <SearchableFilter
                    title="Categories Two"
                    apiEndpoint="/api/CategoriesTwo"
                    dataKey="categoryTwoName"
                    placeholder="Sub-category..."
                    reduxSelector={(s) => s.orebiReducer.checkedCategoriesTwo}
                    toggleAction={toggleCategoryTwo}
                  />
                  <SearchableFilter
                    title="Brand"
                    apiEndpoint="/api/Brands"
                    dataKey="brandName"
                    placeholder="Brand..."
                    reduxSelector={(s) => s.orebiReducer.checkedBrands}
                    toggleAction={toggleBrand}
                  />
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-100 my-5" />

              {/* Section: Extended */}
              <div className="mb-2">
                <button
                  onClick={() => setShowMoreFilters(!showMoreFilters)}
                  className="flex items-center gap-2 w-full group mb-4"
                >
                  <span className="w-1 h-4 rounded-full bg-gray-200 group-hover:bg-indigo-400 transition-colors" />
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400 group-hover:text-indigo-600 transition-colors">
                    Extended Filters
                  </p>
                  <div className="ml-auto text-gray-300 group-hover:text-indigo-400 transition-colors">
                    {showMoreFilters ? (
                      <ChevronUp size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                  </div>
                </button>

                {showMoreFilters && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <SearchableFilter
                      title="Level 3"
                      apiEndpoint="/api/CategoriesThree"
                      dataKey="categoryThreeName"
                      placeholder="Search..."
                      reduxSelector={(s) =>
                        s.orebiReducer.checkedCategoriesThree
                      }
                      toggleAction={toggleCategoryThree}
                    />
                    <SearchableFilter
                      title="Level 4"
                      apiEndpoint="/api/CategoriesFour"
                      dataKey="categoryFourName"
                      placeholder="Search..."
                      reduxSelector={(s) =>
                        s.orebiReducer.checkedCategoriesFour
                      }
                      toggleAction={toggleCategoryFour}
                    />
                    <SearchableFilter
                      title="Level 5"
                      apiEndpoint="/api/CategoriesFive"
                      dataKey="categoryFiveName"
                      placeholder="Search..."
                      reduxSelector={(s) =>
                        s.orebiReducer.checkedCategoriesFive
                      }
                      toggleAction={toggleCategoryFive}
                    />
                  </div>
                )}
              </div>

              {/* Section: Price Range */}
              {priceRange[0] < priceRange[1] && (
                <>
                  <div className="h-px bg-gray-100 my-5" />
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-1 h-4 rounded-full bg-indigo-500" />
                    <p className="text-[10px] font-bold tracking-widest uppercase text-gray-400">
                      Price Range
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl border border-gray-100 p-5">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Tag size={12} />
                        <span>Drag to set range</span>
                      </div>
                      <span className="text-xs font-mono font-medium text-indigo-600 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-md">
                        {formatCurrency(currentRange[0])} —{" "}
                        {formatCurrency(currentRange[1])}
                      </span>
                    </div>
                    <div className="px-2 pb-1">
                      <Range
                        step={1}
                        min={priceRange[0]}
                        max={priceRange[1]}
                        values={currentRange}
                        onChange={setCurrentRange}
                        renderTrack={({ props, children }) => (
                          <div
                            {...props}
                            style={{
                              ...props.style,
                              height: "4px",
                              width: "100%",
                              position: "relative",
                            }}
                            className="rounded-full bg-gray-200"
                          >
                            <div
                              style={{
                                position: "absolute",
                                height: "4px",
                                borderRadius: "4px",
                                background: "#4f46e5",
                                left: `${((currentRange[0] - priceRange[0]) / (priceRange[1] - priceRange[0])) * 100}%`,
                                width: `${((currentRange[1] - currentRange[0]) / (priceRange[1] - priceRange[0])) * 100}%`,
                              }}
                            />
                            {children}
                          </div>
                        )}
                        renderThumb={({ props }) => (
                          <div
                            {...props}
                            style={{ ...props.style }}
                            className="w-4 h-4 rounded-full bg-white border-2 border-indigo-600 shadow-md outline-none cursor-pointer hover:scale-110 transition-transform"
                          />
                        )}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl shrink-0">
              <p className="text-xs text-gray-400">
                Filters apply to the product catalog below.
              </p>
              <div className="flex gap-3">
                {/* Reset Filters — only shown when filters are active */}
                {hasActiveFilters && (
                  <button
                    onClick={handleResetFilters}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-red-500 bg-red-50 border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors"
                  >
                    <RotateCcw size={13} />
                    Reset Filters
                  </button>
                )}
                <button
                  onClick={() => setIsFilterSectionVisible(false)}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsFilterSectionVisible(false)}
                  className="px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm hover:shadow-md transition-all"
                >
                  Apply &amp; View Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CUSTOMER MODAL ── */}
      {isCustomerModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col border border-gray-200">
            <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-b border-gray-100 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
                  <Users size={15} />
                </div>
                <span className="font-bold text-gray-900">
                  Customer Details
                </span>
              </div>
              <button
                onClick={() => setIsCustomerModalOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all"
              >
                <X size={18} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <FormInputs
                onCustomerSelect={handleCustomerSelect}
                onSave={handleSaveCustomerForm}
                onCancel={() => setIsCustomerModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── FLOATING CHECKOUT ── */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => setIsCheckoutModalOpen(true)}
          className="flex items-center gap-3 bg-gray-900 text-white pl-4 pr-5 py-3 rounded-full shadow-xl shadow-gray-900/30 hover:bg-black hover:scale-[1.02] hover:shadow-2xl active:scale-95 transition-all duration-200"
        >
          <div className="relative">
            <div className="bg-gray-800 p-1.5 rounded-full">
              <ShoppingCart size={18} />
            </div>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-indigo-500 rounded-full flex items-center justify-center text-[9px] font-bold border-2 border-gray-900">
                {totalItems}
              </span>
            )}
          </div>
          <div className="flex flex-col items-start leading-none gap-0.5">
            <span className="text-[9px] font-mono text-gray-400 uppercase tracking-widest">
              {totalItems} item{totalItems !== 1 ? "s" : ""}
            </span>
            <span className="text-sm font-bold tracking-tight">Checkout</span>
          </div>
        </button>
      </div>

      {/* ── CHECKOUT MODAL ── */}
      {isCheckoutModalOpen && (
        <ProductPos
          isCheckoutView={true}
          onBackToProducts={() => setIsCheckoutModalOpen(false)}
          hideCustomerEdit={true}
        />
      )}
    </div>
  );
};

export default AllPos;
